import React from 'react';

// Simple receipt generator for UPS shipments
// Expects billing/summary data from Shipping screen or history

const numberToWords = (num) => {
  const a = [
    '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
    'Seventeen', 'Eighteen', 'Nineteen'
  ];
  const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  if ((num = num.toString()).length > 9) return 'Overflow';
  const n = ('000000000' + num).substr(-9).match(/^(\d{2})(\d{2})(\d{2})(\d{1})(\d{2})$/);
  if (!n) return '';
  let str = '';
  str += (n[1] !== '00') ? (a[Number(n[1])] || (b[n[1][0]] + ' ' + a[n[1][1]])) + ' Crore ' : '';
  str += (n[2] !== '00') ? (a[Number(n[2])] || (b[n[2][0]] + ' ' + a[n[2][1]])) + ' Lakh ' : '';
  str += (n[3] !== '00') ? (a[Number(n[3])] || (b[n[3][0]] + ' ' + a[n[3][1]])) + ' Thousand ' : '';
  str += (n[4] !== '0') ? (a[Number(n[4])] || (b[n[4][0]] + ' ' + a[n[4][1]])) + ' Hundred ' : '';
  str += (n[5] !== '00') ? ((str !== '') ? 'and ' : '') +
    (a[Number(n[5])] || (b[n[5][0]] + ' ' + a[n[5][1]])) : '';

  if (str !== '') {
    str = str.trim() + ' Only';
  } else if (num === '0') {
    str = 'Zero Only';
  }
  return str;
};

export const openReceiptWindow = ({
  receiptNo,
  invoiceNo,
  trackingNumber,
  amountPKR,
  receiverName,
  createdBy,
  paymentMethod = '',
  transactionId = '',
  drawnOn = '',
  accountNo = '',
  paymentDate = '',
  receiptEnabled = true,
  packages = [],
  countryOfOrigin = '',
  totalWeight = 0
}) => {
  const today = new Date().toLocaleDateString('en-GB');
  const displayDate = paymentDate || today;
  const amountNumber = parseFloat(String(amountPKR).replace(/[^0-9.\-]/g, '')) || 0;
  const amountWords = numberToWords(Math.floor(amountNumber)) || '';
  const formattedTotalAmount = amountNumber.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  // Receipt number — use only receiptNo (NOT invoiceNo which is for commercial invoice)
  const displayReceiptNo = receiptNo || 'N/A';
  const packageCount = Array.isArray(packages) ? packages.length : 0;
  // Display billingWeight (passed as totalWeight parameter), fallback to calculated weight from packages
  const displayWeight = totalWeight 
    ? parseFloat(totalWeight).toFixed(2)
    : (Array.isArray(packages) && packages.length > 0 
      ? packages.reduce((sum, pkg) => sum + (parseFloat(pkg.weight) || 0), 0).toFixed(2)
      : '0.00');
  const displayCountry = countryOfOrigin || 'N/A';

  const receiptHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Receipt - Universal Logistics</title>
        <style>
          body {
            font-family: Arial, "Helvetica Neue", Helvetica, sans-serif;
            margin: 0;
            padding: 20px;
            background: #fff;
            color: #000;
            font-size: 13px;
          }
          .receipt-container {
            width: 100%;
            max-width: 800px;
            margin: 0 auto;
            padding: 20px;
          }
          .receipt-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 15px; }
          .header-left { flex-basis: 60%; }
          .company-name { font-family: "Arial Black", Arial, sans-serif; font-weight: 900; font-size: 16px; margin: 0; }
          .company-subtitle { font-size: 12px; margin: 0; }
          .company-address { font-size: 11px; margin: 0 0 2px 0; color: #333; }
          .header-right { flex-basis: 40%; text-align: right; font-size: 12px; line-height: 1.5; }
          .header-right p { margin: 0; }

          .receipt-title-section { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; border-top: 1px solid #eee; border-bottom: 1px solid #eee; padding: 10px 0; }
          .receipt-no-block { font-size: 13px; }
          .receipt-no-label { font-weight: normal; font-size: 12px; color: #555; }
          .receipt-no-value { font-family: 'Courier New', Courier, monospace; font-weight: bold; font-size: 24px; letter-spacing: 2px; color: #222; margin-left: 10px; padding: 2px 5px; border: 2px solid #000; }
          .receipt-title { font-size: 20px; font-weight: bold; text-decoration: underline; text-transform: uppercase; padding-bottom: 3px; }
          .receipt-date-block { font-size: 13px; text-align: right; }
          .receipt-date-label { font-size: 12px; color: #555; }
          .receipt-date-value { font-weight: bold; font-size: 15px; }
          .receipt-no { font-size: 14px; display: flex; align-items: center; }
          .receipt-date { font-size: 14px; display: flex; align-items: flex-end; }
          .line-value { font-weight: bold; padding: 0 5px; }

          .receipt-body { margin-top: 10px; }
          .form-row { display: flex; align-items: flex-end; margin-bottom: 20px; font-size: 14px; }
          .form-row .label { white-space: nowrap; margin-right: 8px; min-width: 150px; color: #555; }
          .line-container { flex-grow: 1; border-bottom: 1.5px solid #000; position: relative; height: 22px; }
          .line-container .line-value { position: absolute; left: 10px; bottom: -2px; background: #fff; padding: 0 5px; font-weight: bold; color: #000; }
          .multi-field-row { display: flex; justify-content: space-between; align-items: flex-end; margin-bottom: 20px; font-size: 14px; }
          .multi-field-row .form-row { margin-bottom: 0; flex-grow: 1; }

          .amount-row { display: flex; align-items: flex-end; margin: 20px 0 12px; }
          .amount-label { font-size: 20px; font-weight: bold; margin-right: 10px; }
          .amount-value { flex-grow: 1; border-bottom: 2px solid #000; position: relative; min-height: 30px; }
          .amount-value .line-value { position: absolute; left: 8px; bottom: 3px; font-size: 18px; font-weight: bold; }

          .payment-section { margin: 10px 0; }
          .payment-row { display: flex; align-items: flex-end; margin-bottom: 14px; font-size: 13px; gap: 12px; }
          .payment-field { display: flex; align-items: flex-end; flex: 1; }
          .payment-field .label { white-space: nowrap; margin-right: 6px; color: #555; font-size: 12px; }
          .payment-field .line { flex-grow: 1; border-bottom: 1.5px solid #000; position: relative; min-height: 20px; }
          .payment-field .line .val { position: absolute; left: 6px; bottom: 2px; font-weight: bold; font-size: 13px; }

          .subject-note { font-size: 11px; font-style: italic; margin-top: 6px; color: #444; }

          .receipt-footer { margin-top: 35px; display: flex; justify-content: space-between; align-items: flex-end; }
          .subject-line { font-size: 12px; font-style: italic; }
          .signature-line { font-size: 13px; font-weight: bold; text-align: right; line-height: 1.6; }
          .generated-by { font-size: 11px; color: #666; font-style: italic; margin-top: 20px; }

          @media print {
            body { margin: 0; padding: 0; }
            .receipt-container { max-width: 100%; width: 100%; margin: 0; padding: 0; border: none; }
          }
        </style>
      </head>
      <body>
        <div class="receipt-container">

          <!-- Header -->
          <div class="receipt-header">
            <div class="header-left">
              <h1 class="company-name">Universal Logistics Services (Pvt.) Ltd.</h1>
              <p class="company-subtitle">Authorised Service Contractor for UPS.</p>
              <p style="font-size: 11px; margin: 5px 0 0 0;">D-79, Kehkashan, Clifton Block 5, Karachi.</p>
            </div>
            <div class="header-right">
              <p>Tel: +92 21 35831127-29</p>
              <p>UAN: 111 560 560</p>
              <p>NTN #: 1343872-7</p>
            </div>
          </div>

          <!-- Receipt No. + RECEIPT + Date -->
          <div class="receipt-title-section">
            <div class="receipt-no">Receipt No. <span class="receipt-no-value">${displayReceiptNo}</span></div>
            <div class="receipt-title">RECEIPT</div>
            <div class="receipt-date">
              <span class="label">Date:</span>
              <div class="line-container" style="flex-grow: 0; width : 120px; border-bottom: none;"><span class="line-value" style="left: 5px;">${displayDate}</span></div>
            </div>
          </div>

          <!-- Body Fields -->
          <div class="receipt-body">
            <div class="form-row">
              <span class="label">Received with thanks from</span>
              <div class="line-container"><span class="line-value">${receiverName || 'N/A'}</span></div>
            </div>
            <div class="form-row">
              <span class="label">The sum of Rupees</span>
              <div class="line-container"><span class="line-value">${amountWords}</span></div>
            </div>
            <div class="form-row">
              <span class="label">On Account of</span>
              <div class="line-container"><span class="line-value">${trackingNumber || 'N/A'}</span></div>
            </div>
            
            <div class="multi-field-row">
              <div class="form-row" style="flex: 1;"><span class="label">By CHEQUE/P.O./CASH/ONLINE</span><div class="line-container"><span class="line-value">${paymentMethod || '__________'}</span></div></div>
              <div class="form-row" style="flex: 0.5; margin-left: 20px;"><span class="label" style="min-width: 50px;">Dated</span><div class="line-container"><span class="line-value">${displayDate}</span></div></div>
            </div>
            
            <div class="form-row">
              <span class="label">Drawn on</span>
              <div class="line-container"><span class="line-value">${drawnOn || ''}</span></div>
            </div>

            <div class="form-row" style="margin-top: 25px; max-width: 350px;">
              <span class="label" style="font-size: 18px; font-weight: bold; min-width: 50px;">Rs.</span>
              <div class="line-container" style="border-bottom: 2px solid #000;"><span class="line-value" style="font-size: 18px; font-weight: bold;">${formattedTotalAmount}</span></div>
            </div>

            <div class="form-row" style="margin-top: 15px;">
              <span class="label">Tnx ID / Check No.*:</span>
              <div class="line-container"><span class="line-value">${transactionId || '__________'}</span></div>
            </div>

            <!-- Shipment Details Below -->
            <div style="margin-top: 25px; padding-top: 15px; border-top: 1px solid #ddd; font-size: 12px; color: #555;">
              <div style="display: flex; justify-content: space-around; text-align: center;">
                <div style="flex: 1;">
                  <div style="font-weight: bold; font-size: 14px; color: #222;">${packageCount}</div>
                  <div style="font-size: 11px; color: #666;">Number of Packages</div>
                </div>
                <div style="flex: 1;">
                  <div style="font-weight: bold; font-size: 14px; color: #222;">${displayWeight} kg</div>
                  <div style="font-size: 11px; color: #666;">Total Weight</div>
                </div>
                <div style="flex: 1;">
                  <div style="font-weight: bold; font-size: 14px; color: #222;">${displayCountry}</div>
                  <div style="font-size: 11px; color: #666;">Country of Destination</div>
                </div>
              </div>
            </div>
          </div>
          
          <div class="generated-by">Generated by: <strong>${createdBy || 'system'}</strong></div>
          
          <div class="receipt-footer">
            <div class="subject-line">*SUBJECT TO REALIZATION</div>
            <div class="signature-line">
              Universal Logistics Services (Pvt.) Ltd.<br>
              Authorised Service Contractor for UPS.
            </div>
          </div>
        </div>
        <script>window.onload = function() { window.print(); }</script>
      </body>
      </html>
  `;

  const win = window.open('', '_blank');
  if (win) {
    win.document.write(receiptHTML);
    win.document.close();
  }
};

const CreateReceipt = ({
  receiptNo,
  invoiceNo,
  trackingNumber,
  amountPKR,
  receiverName,
  createdBy,
  paymentMethod,
  transactionId,
  drawnOn,
  accountNo,
  paymentDate,
  receiptEnabled = true,
  packages = [],
  countryOfOrigin = '',
  totalWeight = 0
}) => {
  const handleGenerate = () => {
    openReceiptWindow({
      receiptNo,
      invoiceNo,
      trackingNumber,
      amountPKR,
      receiverName,
      createdBy,
      paymentMethod,
      transactionId,
      drawnOn,
      accountNo,
      paymentDate,
      receiptEnabled,
      packages,
      countryOfOrigin,
      totalWeight
    });
  };

  const isDisabled = !amountPKR || !receiptEnabled;
  const disabledTitle = !amountPKR 
    ? 'Amount not available yet' 
    : !receiptEnabled 
    ? 'Receipts are disabled for this company' 
    : 'Generate Receipt';

  return (
    <button
      className={`btn btn-outline`}
      onClick={handleGenerate}
      disabled={isDisabled}
      title={disabledTitle}
      style={{ opacity: isDisabled ? 0.5 : 1, cursor: isDisabled ? 'not-allowed' : 'pointer' }}
    >
      📄 Receipt
    </button>
  );
};

export default CreateReceipt;
